import dayjs from 'dayjs';

import {i18n} from '@yourcoach/i18n';

import type {BNUserProfileUpdateParams} from '../../api/types/bnrks/methods';
import type {CoreUserProfileUpdateParams} from '../../api/types/core/methods';
import type {AlertButton} from '../../components/alert/components/Alert/types';

import {currentUserStore} from '../../api/user';
import DangerSvg from '../../assets/icons/primary/Danger.svg';
import {alert} from '../../components/alert';
import {isIamCoach} from '../../utils/entity/isIamCoach';
import {envManager} from '../../utils/env';
import {logger} from '../../utils/logger';
import {TestID} from '../../utils/test-utils/data-test-id';

let inProgress = false;

type UpdateParams = Pick<
  BNUserProfileUpdateParams | CoreUserProfileUpdateParams,
  'private_profile' | 'timezone'
>;

const updateUserProfile = (params: UpdateParams) =>
  currentUserStore.update({
    b2b: params,
    // @ts-expect-error: TODO: describe why
    core: params,
  });

export const syncTimeZoneService = (config: {onEditScheduleClick: () => any}) =>
  new Promise<void>(async resolve => {
    if (inProgress) {
      resolve();

      return;
    }

    const innerResolve = () => {
      inProgress = false;

      resolve();
    };

    if (
      !currentUserStore.user ||
      (currentUserStore.user?.metadata?.onboarding &&
        !currentUserStore.user?.metadata?.onboarding.is_finished)
    ) {
      innerResolve();

      return;
    }

    const sysTimezone = dayjs.tz.guess();

    const isEmptyOrDefaultTimezone =
      !currentUserStore.user.timezone ||
      currentUserStore.user.timezone === 'UTC';

    const isNotCurrentTimezone = currentUserStore.user.timezone !== sysTimezone;

    if (isEmptyOrDefaultTimezone) {
      const params: UpdateParams = {
        private_profile: {
          ...currentUserStore.user?.private_profile,
          timezone: sysTimezone,
        },
        timezone: sysTimezone,
      };

      await updateUserProfile(params);

      innerResolve();
    } else if (isNotCurrentTimezone) {
      try {
        const isCoach = isIamCoach();

        const prevTimezone = currentUserStore.user.timezone;

        if (!prevTimezone || !sysTimezone) {
          innerResolve();

          return;
        }

        const params: UpdateParams = {
          private_profile: {
            ...currentUserStore.user?.private_profile,
            timezone: sysTimezone,
          },
          timezone: sysTimezone,
        };

        await updateUserProfile(params);

        const now = dayjs().toISOString();

        const isActualTimeChanged =
          dayjs.tz(now, prevTimezone).toString() !==
          dayjs.tz(now, sysTimezone).toString();

        if (!isActualTimeChanged) {
          innerResolve();

          return;
        }

        if (envManager.isB2bWidget || envManager.isDtc) {
          innerResolve();

          return;
        }

        alert.show({
          buttons: Array.from<AlertButton | null>([
            isCoach
              ? {
                  label: i18n.t(
                    'notifications:timezone_changed.edit_schedule_button',
                  ),
                  onClick: () => {
                    config.onEditScheduleClick();

                    innerResolve();
                  },
                  testID: TestID.change_time_zone.button.edit_schedule,
                }
              : null,
            {
              label:
                /**
                 * @i18n_parser
                 * t('notifications:timezone_changed.ok_button.coach')
                 * t('notifications:timezone_changed.ok_button.client')
                 */
                i18n.t(
                  `notifications:timezone_changed.ok_button.${isCoach ? 'coach' : 'client'}`,
                ),
              onClick: () => {
                innerResolve();
              },
              scheme: 'secondary',
              testID: TestID.change_time_zone.button.ok,
            },
          ]).filter(Boolean),
          icon: DangerSvg,
          iconColor: 'error',
          /**
           * @i18n_parser
           * t('notifications:timezone_changed.message.coach')
           * t('notifications:timezone_changed.message.client')
           */
          message: i18n.t(
            `notifications:timezone_changed.message.${isCoach ? 'coach' : 'client'}`,
          ),
          testID: TestID.change_time_zone.block.alert,
          title: i18n.t('notifications:timezone_changed.title'),
          variant: 'promo',
        });
      } catch (error) {
        logger.error(error);

        innerResolve();
      }
    } else {
      innerResolve();
    }
  });
